import React, { useEffect, useMemo } from 'react';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { useLocation, useHistory, NavLink } from 'react-router-dom';
import { connect } from 'react-redux';
import Select from 'react-select';
import DATE_FORMAT from '../../components/constants/dateFormat';
import utils from '../../utils/utils';
import Loader from '../../components/loader';
import { useDeliveryDays, useRoutePlanSetups } from './apiHooks';
import { parse, stringify } from 'qs';
import { Route } from 'react-router';
import Helmet from 'react-helmet';
import AdminRoutePlanV2Setups from './adminRoutePlanV2Setups';
import AdminRoutePlanV2DeliveryDayRoutes from './adminRoutePlanV2DeliveryDayRoutes';
import ErrorMessage from '../../components/errorMessage';
import AdminRoutePlanV2Optimise from './adminRoutePlanV2Optimise';
import AdminRoutePlanV2Addresses from './adminRoutePlanV2Addresses';

function useUrlParameterState(param) {
  const { search } = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(search), [search]);
  const id = queryParams.get(param);

  const history = useHistory();
  const searchObj = parse(search, { ignoreQueryPrefix: true });

  const setNewValue = (newId, resetOtherQueryParams) => {
    searchObj[param] = newId;
    const newObj = resetOtherQueryParams ? { [param]: newId } : searchObj;
    history.replace({
      search: stringify(newObj, { ignoreQueryPrefix: false }),
    });
  };

  return [id, setNewValue];
}

const AdminRoutePlanV2 = ({ match }) => {
  const [selectedRoutePlanSetupPickupSlotIdFromUrl, setSelectedRoutePlanSetupPickupSlotId] =
    useUrlParameterState('routePlanSetupPickupSlotId');
  const [selectedDeliveryDayIdFromUrl, setSelectedDeliveryDayId] = useUrlParameterState('deliveryDayId');
  const { isLoading: loadingDeliveryDays, deliveryDays } = useDeliveryDays({
    showAll: true,
    sortBy: 'deliveryDateDesc',
    max: 20,
  });

  const { search } = useLocation();

  const selectedDeliveryDay =
    selectedDeliveryDayIdFromUrl && deliveryDays?.length
      ? deliveryDays.find((dd) => `${dd.id}` === selectedDeliveryDayIdFromUrl)
      : null;

  const selectedDeliveryDayOption = selectedDeliveryDay
    ? {
        value: selectedDeliveryDay.id,
        label: `${utils.getFormattedDateFromString(
          selectedDeliveryDay.deliveryDate,
          DATE_FORMAT.displayWeekdayAndDate
        )}`,
      }
    : null;

  const {
    isLoading: isRoutePlanSetupsLoading,
    isError: isRoutePlanSetupsError,
    error: routePlanSetupsError,
    routePlanSetups,
  } = useRoutePlanSetups({
    deliveryDayId: selectedDeliveryDay?.id,
  });

  const pickupSlots = routePlanSetups ? routePlanSetups.map((rps) => rps.pickupSlots).flat() : [];

  const selectedPickupSlot =
    selectedRoutePlanSetupPickupSlotIdFromUrl && pickupSlots
      ? pickupSlots.find((ps) => `${ps.id}` === selectedRoutePlanSetupPickupSlotIdFromUrl)
      : null;

  const selectedPickupSlotOption = selectedPickupSlot
    ? {
        value: selectedPickupSlot.id,
        label: `${selectedPickupSlot.name} ${utils.getFormattedDateRangeFromStrings(
          selectedPickupSlot.pickupFrom,
          selectedPickupSlot.pickupTo
        )} (#${selectedPickupSlot.id})`,
      }
    : null;

  const { formatMessage } = useIntl();

  return (
    <div className="m-b-6">
      <Loader show={loadingDeliveryDays || isRoutePlanSetupsLoading} />
      {isRoutePlanSetupsError ? <ErrorMessage error={utils.getErrorMessageFromResponse(routePlanSetupsError)} /> : ''}
      <Helmet>
        <title>{formatMessage({ id: 'admin.route.planning.header' })}</title>
      </Helmet>
      <h2 className="">
        <FormattedMessage id={'admin.route.planning.header'} />
      </h2>
      <div className="row">
        <div className="col-12 col-md-6">
          {deliveryDays ? (
            <div className="form-group">
              <label htmlFor="ddSelect">
                <FormattedMessage id={'admin.route.planning.selectors.delivery.day.label'} />
              </label>
              <Select
                name="ddSelect"
                value={selectedDeliveryDayOption}
                onChange={(option) => setSelectedDeliveryDayId(option.value, true)}
                options={deliveryDays.map((deliveryDay) => ({
                  value: deliveryDay.id,
                  label: `${utils.getFormattedDateFromString(
                    deliveryDay.deliveryDate,
                    DATE_FORMAT.displayWeekdayAndDate
                  )}`,
                }))}
              />
            </div>
          ) : (
            ''
          )}
        </div>
        <div className="col-12 col-md-6">
          {selectedDeliveryDay ? (
            <div className="form-group">
              <label htmlFor="ddSelect">
                <FormattedMessage id={'admin.route.planning.selectors.route.plan.setup.pickup.slot.label'} />
              </label>
              <Select
                name="pickupSlotSelect"
                value={selectedPickupSlotOption}
                onChange={(option) => setSelectedRoutePlanSetupPickupSlotId(option.value)}
                options={
                  pickupSlots
                    ? pickupSlots.map((ps) => ({
                        value: ps.id,
                        label: `${ps.name} ${utils.getFormattedDateRangeFromStrings(ps.pickupFrom, ps.pickupTo)} (#${
                          ps.id
                        })`,
                      }))
                    : []
                }
              />
            </div>
          ) : (
            ''
          )}
        </div>
      </div>

      <div className="m-b-3">
        <NavLink to={`${match.path}${search}`} exact className="link m-r-2">
          <FormattedMessage id={'admin.route.planning.buttons.all.delivery.day.routes'} />
        </NavLink>
        <NavLink to={`${match.path}/optimise${search}`} className="link m-r-2">
          <FormattedMessage id={'admin.route.planning.buttons.optimise'} />
        </NavLink>
        <NavLink to={`${match.path}/setup${search}`} className="link m-r-2">
          <FormattedMessage id={'admin.route.planning.buttons.setup'} />
        </NavLink>
        <NavLink to={`${match.path}/addresses${search}`} className="link m-r-2">
          <FormattedMessage id={'admin.route.planning.buttons.addresses'} />
        </NavLink>
      </div>

      <Route
        exact
        path={`${match.path}`}
        render={(props) =>
          selectedDeliveryDay ? (
            <AdminRoutePlanV2DeliveryDayRoutes selectedDeliveryDay={selectedDeliveryDay} {...props} />
          ) : (
            <div>
              <FormattedMessage id={'admin.route.plan.setup.pick.delivery.day'} />
            </div>
          )
        }
      />

      <Route
        path={`${match.path}/setup`}
        render={(props) =>
          selectedDeliveryDay ? (
            <AdminRoutePlanV2Setups deliveryDay={selectedDeliveryDay} routePlanSetups={routePlanSetups} {...props} />
          ) : (
            <div>
              <FormattedMessage id={'admin.route.plan.setup.pick.delivery.day'} />
            </div>
          )
        }
      />
      <Route
        path={`${match.path}/optimise`}
        render={(props) =>
          selectedPickupSlot && selectedDeliveryDay ? (
            <AdminRoutePlanV2Optimise deliveryDay={selectedDeliveryDay} pickupSlot={selectedPickupSlot} {...props} />
          ) : (
            <div>
              <FormattedMessage id={'admin.route.plan.setup.pick.route.plan.setup.pickup.slot'} />
            </div>
          )
        }
      />
      <Route
        path={`${match.path}/addresses`}
        render={(props) =>
          selectedDeliveryDay ? (
            <AdminRoutePlanV2Addresses deliveryDay={selectedDeliveryDay} {...props} />
          ) : (
            <div>
              <FormattedMessage id={'admin.route.plan.setup.pick.delivery.day'} />
            </div>
          )
        }
      />
    </div>
  );
};

AdminRoutePlanV2.propTypes = {};

AdminRoutePlanV2.defaultProps = {};

function mapStateToProps(state) {
  return {
    currentUser: state.currentUser.user,
  };
}

const mapDispatchToProps = {};

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(AdminRoutePlanV2));
